/** Leader carry-forward is a separate output from coach craft evaluation. */

type AgreementFields = { discussed: string[]; nextFocus: string[]; nextSession: string[] };
type PlanFields = { whatDiscussed: string[]; nextFocus: string[]; nextSession: string[]; actionItems: string[]; reflectionPrompts: string[] };
const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
const normalize = (value: string) => value.replace(/\s+/g, " ").trim();
const lines = (value: string[]) => [...new Set(value.map(normalize).filter(Boolean))].slice(0, 4);

export function readLeaderSessionAgreement(analysis: unknown): AgreementFields | null {
  const raw = asRecord(asRecord(analysis)?.leader_agreement);
  if (!raw) return null;
  const fields = [raw.what_we_discussed, raw.next_focus_before_next_conversation, raw.what_we_will_review_next_session];
  if (!fields.every(value => Array.isArray(value) && value.every(line => typeof line === "string"))) return null;
  return { discussed: lines(fields[0] as string[]), nextFocus: lines(fields[1] as string[]), nextSession: lines(fields[2] as string[]) };
}

/** Only generated seeds are repaired. Saved, coach-authored plans stay authoritative. */
export function removeLegacyCoachFeedbackFromGeneratedPlan<T extends PlanFields>(
  plan: T, analysis: unknown, summaryText?: string | null,
): T {
  const raw = asRecord(analysis);
  const report = asRecord(raw?.report_v1);
  const summary = asRecord(report?.conversation_summary);
  const insights = asRecord(report?.coaching_insights);
  const next = asRecord(report?.recommended_next_step);
  // These exact fields were used by the old fallback. Do not guess audience from wording.
  const privateCopies = new Set([summaryText, raw?.summary_text, summary?.summary_text,
    raw?.key_insight, insights?.key_insight, raw?.next_experiment, next?.next_experiment]
    .filter((value): value is string => typeof value === "string").map(normalize));
  const explicit = readLeaderSessionAgreement(analysis);
  if (explicit) for (const value of [...explicit.discussed, ...explicit.nextFocus, ...explicit.nextSession]) privateCopies.delete(normalize(value));
  const clean = (values: string[]) => values.filter(value => !privateCopies.has(normalize(value)));
  return { ...plan, whatDiscussed: clean(plan.whatDiscussed), nextFocus: clean(plan.nextFocus),
    nextSession: clean(plan.nextSession), actionItems: clean(plan.actionItems), reflectionPrompts: clean(plan.reflectionPrompts) };
}
